import { motion } from "framer-motion";
import { Brush, Layers, Zap, ShieldCheck, UserRound, HeartHandshake } from "lucide-react";

const REASONS = [
    {
        icon: Brush,
        title: "Bespoke craft",
        description: "No templates, no shortcuts. Every product is designed from a blank canvas around your brand and users.",
        testid: "why-card-bespoke-craft",
    },
    {
        icon: Layers,
        title: "Full-stack mastery",
        description: "Design and engineering under one roof means nothing is lost in translation between teams.",
        testid: "why-card-full-stack",
    },
    {
        icon: Zap,
        title: "Rapid delivery cycles",
        description: "Tight sprints and weekly demos keep your product moving — and your launch date honest.",
        testid: "why-card-rapid-delivery",
    },
    {
        icon: ShieldCheck,
        title: "Enterprise-grade quality",
        description: "Security, accessibility and performance are baked into every build, not bolted on after.",
        testid: "why-card-enterprise-quality",
    },
    {
        icon: UserRound,
        title: "Direct founder access",
        description: "You work with the people actually building your product. No account managers, no telephone games.",
        testid: "why-card-founder-access",
    },
    {
        icon: HeartHandshake,
        title: "Client-first partnership",
        description: "We measure success by your outcomes — long after launch day, we're still in your corner.",
        testid: "why-card-client-first",
    },
];

export const WhyUs = () => {
    return (
        <section id="why-us" className="relative py-20 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8 }}
                    className="max-w-2xl"
                >
                    <div className="font-mono-brand text-xs tracking-[0.25em] uppercase font-semibold text-violet-600 mb-4" data-testid="why-eyebrow">
                        Why choose us
                    </div>
                    <h2 className="font-display font-extrabold tracking-tight text-slate-900 text-2xl sm:text-3xl lg:text-4xl leading-snug">
                        The agency you'd build, <span className="gradient-text">if you could.</span>
                    </h2>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {REASONS.map((reason, i) => (
                        <motion.div
                            key={reason.title}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                        >
                            <div className="gradient-border-card h-full" data-testid={reason.testid}>
                                <div className="h-full rounded-[1.2rem] bg-white/85 backdrop-blur-xl p-7">
                                    <div className="h-11 w-11 rounded-xl gradient-bg flex items-center justify-center shadow-lg shadow-violet-500/20">
                                        <reason.icon size={20} className="text-white" />
                                    </div>
                                    <h3 className="font-display font-bold text-lg text-slate-900 mt-5">{reason.title}</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed mt-2.5">{reason.description}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
